import React, { useState } from "react";
import { Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { TIERS, canAccess } from "@/lib/tiers";
import { Lock, Calendar } from "lucide-react";
import MetaBadges from "@/components/MetaBadges";
import RequestedByBadge from "@/components/RequestedByBadge";
import { useAuth } from "@/context/AuthContext";

const INITIAL_CARD_TAGS = 5;

export default function PostCard({ post }) {
  const { user } = useAuth();
  const tier = TIERS[post.access_level] || TIERS.public;
  const userRole = user?.role || "public";
  // Reuses the same hierarchy as download-link gating (canAccess matches
  // backend auth.user_can_access exactly). Cards are NEVER hidden — we
  // just blur the thumbnail and overlay the upsell so users discover
  // the release and can upgrade.
  const accessible = canAccess(userRole, post.access_level || "public");
  const locked = !accessible;
  const tags = post.tags || [];
  const [showAllTags, setShowAllTags] = useState(false);
  const visibleTags = showAllTags ? tags : tags.slice(0, INITIAL_CARD_TAGS);
  const hasMoreTags = tags.length > INITIAL_CARD_TAGS;

  const toggleTags = (e) => {
    // Prevent the parent <Link> from navigating when the user expands/collapses.
    e.preventDefault();
    e.stopPropagation();
    setShowAllTags((v) => !v);
  };

  return (
    <Link
      to={`/posts/${post.slug}`}
      data-testid={`post-card-${post.slug}`}
      className="group relative block overflow-hidden rounded-xl border border-white/10 bg-zinc-950 hover:border-rose-500/40 hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-[16/10] bg-zinc-900 overflow-hidden">
        {post.thumbnail_url ? (
          <img
            src={fileUrl(post.thumbnail_url)}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            data-testid={`post-thumb-${post.slug}`}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-zinc-900 via-rose-950/30 to-black" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Lock overlay — only when the user lacks access. */}
        {locked && (
          <div
            data-testid={`card-locked-${post.slug}`}
            className="absolute inset-0 flex items-center justify-center bg-black/45"
          >
            <div className="flex flex-col items-center text-center px-3">
              <div className="w-10 h-10 rounded-full bg-rose-600/25 border border-rose-500/50 flex items-center justify-center mb-2 shadow-lg shadow-rose-900/40">
                <Lock className="w-5 h-5 text-rose-200" />
              </div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-rose-100">
                {tier.label} Required
              </div>
            </div>
          </div>
        )}

        {post.status === "draft" && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-amber-900/80 border border-amber-500/40 text-amber-200 text-[10px] font-semibold uppercase">Draft</div>
        )}
        {post.featured && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-rose-600/80 border border-rose-400/40 text-white text-[10px] font-semibold uppercase">Featured</div>
        )}
        {post.version && !locked && (
          <div className="absolute bottom-2 right-2 text-[10px] text-zinc-300 bg-black/60 px-2 py-0.5 rounded">v{post.version}</div>
        )}
      </div>

      <div className="p-4">
        {/* Access level badge */}
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] px-2 py-0.5 rounded ${tier.chip} uppercase tracking-wider inline-flex items-center`}>
            {(locked || post.access_level !== "public") && <Lock className="w-2.5 h-2.5 mr-1" />} {tier.label}
          </span>
        </div>

        {/* Engine + Status metadata badges (NOT tags) */}
        <div className="mt-1.5">
          <MetaBadges engine={post.engine} dev_status={post.dev_status} />
        </div>

        {/* Title — still visible when locked for discovery */}
        <div className="mt-2 font-display font-semibold text-white truncate">{post.title}</div>

        {/* Description — hide when locked */}
        {!locked && (
          <div className="mt-1 text-xs text-zinc-500 line-clamp-2 min-h-[2rem]">
            {(post.description || "").replace(/<[^>]+>/g, "").slice(0, 110)}
          </div>
        )}

        {/* Tags (first 5, with show more/less) */}
        {tags.length > 0 && (
          <div className="mt-3 flex gap-1.5 flex-wrap items-center min-h-[1.5rem]">
            {visibleTags.map((t) => (
              <span key={t} className="px-1.5 py-0.5 rounded text-[11px] bg-white/5 border border-white/10 text-zinc-300">{t}</span>
            ))}
            {hasMoreTags && (
              <button
                type="button"
                onClick={toggleTags}
                data-testid={`card-tags-toggle-${post.slug}`}
                className="px-1.5 py-0.5 rounded text-[11px] bg-rose-900/30 border border-rose-500/30 text-rose-200 hover:bg-rose-800/40 transition"
              >
                {showAllTags ? "Show Less ▲" : `Show More ▼ (${tags.length - INITIAL_CARD_TAGS})`}
              </button>
            )}
          </div>
        )}

        {/* Date — visible even when locked (per spec). */}
        {post.release_date && (
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-500">
            <Calendar className="w-3 h-3" />{post.release_date}
          </div>
        )}

        {/* "Requested by" supporter credit — additive, optional, visible even when locked. */}
        {post.requested_by_user && (
          <div className="block">
            <RequestedByBadge user={post.requested_by_user} size="sm" />
          </div>
        )}
      </div>
    </Link>
  );
}
